import React, { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '../component/Button';

const publicAsset = (path: string) => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
};

/** BO `merchant` values allowed for local disk fallback (`public/maintenance/<code>/`). */
const MERCHANT_ASSET_CODES = new Set(['J1', 'F1', 'P1', 'T1', 'F1AFF', 'J1AFF', 'P1AFF', 'T1AFF']);

/**
 * Temporary local fallback until BO provides imageUrl/backgroundImageUrl.
 * Assets: `public/maintenance/<merchant>/hero.png` and `bg.png` (merchant from getInfo, e.g. J1).
 */
function resolveMerchantAssetFolder(merchant: unknown): string | null {
    if (typeof merchant !== 'string') return null;
    const m = merchant.trim();
    if (!m) return null;
    return MERCHANT_ASSET_CODES.has(m) ? m : null;
}

function maintenanceLocalHeroPath(folder: string | null): string | null {
    if (!folder) return null;
    return publicAsset(`/maintenance/${folder}/hero.png`);
}

function maintenanceLocalBgPath(folder: string | null): string | null {
    if (!folder) return null;
    return publicAsset(`/maintenance/${folder}/bg.png`);
}

type MaintenanceW2CFromStore = {
    message?: string;
    supportEmail?: string;
    imageUrl?: string;
    backgroundImageUrl?: string;
    title?: string;
    body?: string;
    footer?: string;
    emailColor?: string;
    merchant?: string;
    buttonText?: string;
    buttonColor?: string;
    buttonUrl?: string;
    pageTitle?: string;
} | null;

const DEFAULT_BUTTON_COLOR = '#0A9D4A';

const normalizeLineBreaks = (value: string) =>
    value.replace(/\\\\n/g, '\n').replace(/\\n/g, '\n');

const TEXT_EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

const DEFAULT_EMAIL_LINK_COLOR = '#0A9D4A';

const BODY_FOOTER_TEXT_CLASS =
    'w-full shrink-0 whitespace-pre-line break-words [overflow-wrap:anywhere] text-pretty text-center text-[14px] font-normal leading-[20px] tracking-[0.01em] text-black lg:tracking-normal lg:text-[14px] lg:leading-[1.35]';

/** Shared width for title, body, footer, and CTA — stays inside the white panel. */
const INNER_CONTENT_CLASS = 'w-full max-w-[360px] mx-auto';

function renderTextWithDetectedEmails(
    text: string,
    emailColor: string,
    keyPrefix: string = 't'
): React.ReactNode {
    const nodes: React.ReactNode[] = [];
    let last = 0;
    let k = 0;
    const re = new RegExp(TEXT_EMAIL_RE.source, 'g');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) {
            nodes.push(
                <span key={`${keyPrefix}-s-${k++}`}>{text.slice(last, m.index)}</span>
            );
        }
        const addr = m[0];
        nodes.push(
            <a
                key={`${keyPrefix}-a-${k++}`}
                href={`mailto:${addr}`}
                className="inline font-normal text-[14px] leading-[14px] underline underline-offset-[2px] transition-opacity hover:opacity-90 lg:text-[14px] lg:leading-[1.35]"
                style={{ color: emailColor }}
            >
                {addr}
            </a>
        );
        last = m.index + addr.length;
    }
    if (last < text.length) {
        nodes.push(<span key={`${keyPrefix}-s-${k++}`}>{text.slice(last)}</span>);
    }
    return nodes.length > 0 ? <>{nodes}</> : text;
}

const WebToCaseMaintenancePage: React.FC = () => {
    const { t } = useTranslation();
    const fromRedux = useSelector(
        (state: { settings: { maintenanceWebToCase: MaintenanceW2CFromStore } }) =>
            state.settings.maintenanceWebToCase
    );

    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) =>
            state.settings.botButtonColor
    );

    const merchantAssetFolder = useMemo(
        () => resolveMerchantAssetFolder(fromRedux?.merchant),
        [fromRedux]
    );

    const title = normalizeLineBreaks(
        (fromRedux && typeof fromRedux.title === 'string' && fromRedux.title.trim()) ||
            t('maintenance.webToCase.mockTitle')
    );

    const body = normalizeLineBreaks(
        (fromRedux && typeof fromRedux.body === 'string' && fromRedux.body.trim()) ||
            (fromRedux && typeof fromRedux.message === 'string' && fromRedux.message.trim()) ||
            t('maintenance.webToCase.mockBody')
    );

    const footer = normalizeLineBreaks(
        (fromRedux && typeof fromRedux.footer === 'string' && fromRedux.footer.trim()) ||
            t('maintenance.webToCase.mockFooter')
    );

    const legacySupportEmail =
        (fromRedux &&
            typeof fromRedux.supportEmail === 'string' &&
            fromRedux.supportEmail.trim()) ||
        '';

    const resolvedEmailColor = useMemo(() => {
        const raw =
            fromRedux &&
            typeof fromRedux.emailColor === 'string' &&
            fromRedux.emailColor.trim()
                ? fromRedux.emailColor.trim()
                : '';
        return raw || DEFAULT_EMAIL_LINK_COLOR;
    }, [fromRedux]);

    const bodyContent = useMemo(
        () => renderTextWithDetectedEmails(String(body ?? ''), resolvedEmailColor, 'body'),
        [body, resolvedEmailColor]
    );

    const footerContent = useMemo(
        () => renderTextWithDetectedEmails(String(footer ?? ''), resolvedEmailColor, 'footer'),
        [footer, resolvedEmailColor]
    );

    const heroImageUrl = useMemo(() => {
        const fromApi =
            fromRedux && typeof fromRedux.imageUrl === 'string' && fromRedux.imageUrl.trim()
                ? fromRedux.imageUrl.trim()
                : '';
        if (fromApi) return fromApi;
        return maintenanceLocalHeroPath(merchantAssetFolder) ?? '';
    }, [fromRedux, merchantAssetFolder]);

    const backgroundImageUrl = useMemo(() => {
        const fromApi =
            fromRedux &&
            typeof fromRedux.backgroundImageUrl === 'string' &&
            fromRedux.backgroundImageUrl.trim()
                ? fromRedux.backgroundImageUrl.trim()
                : '';
        if (fromApi) return fromApi;
        return maintenanceLocalBgPath(merchantAssetFolder) ?? '';
    }, [fromRedux, merchantAssetFolder]);

    const buttonText = useMemo(() => {
        const fromApi =
            fromRedux &&
            typeof fromRedux.buttonText === 'string' &&
            fromRedux.buttonText.trim()
                ? fromRedux.buttonText.trim()
                : '';
        return fromApi || t('maintenance.webToCase.backToHome');
    }, [fromRedux, t]);

    const buttonColor = useMemo(() => {
        const raw =
            fromRedux &&
            typeof fromRedux.buttonColor === 'string' &&
            fromRedux.buttonColor.trim()
                ? fromRedux.buttonColor.trim()
                : '';
        return raw || DEFAULT_BUTTON_COLOR;
    }, [fromRedux]);

    const buttonUrl = useMemo(() => {
        const fromApi =
            fromRedux &&
            typeof fromRedux.buttonUrl === 'string' &&
            fromRedux.buttonUrl.trim()
                ? fromRedux.buttonUrl.trim()
                : '';
        return fromApi || window.location.origin;
    }, [fromRedux]);

    const pageTitle = useMemo(() => {
        const fromApi =
            fromRedux &&
            typeof fromRedux.pageTitle === 'string' &&
            fromRedux.pageTitle.trim()
                ? fromRedux.pageTitle.trim()
                : '';
        return fromApi || t('maintenance.webToCase.pageTitle');
    }, [fromRedux, t]);

    const handleBackToHome = useCallback(() => {
        window.location.href = buttonUrl;
    }, [buttonUrl]);

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <div
                className="fixed inset-0 z-[1] min-h-[100dvh] min-h-screen w-screen overflow-y-auto"
                style={{
                    backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(248,250,252,0.50) 45%, rgba(241,245,249,0.55) 100%), url(${backgroundImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="flex min-h-[100dvh] min-h-screen w-full items-stretch justify-center px-0 py-0 lg:items-center lg:justify-center lg:px-6 lg:py-10">
                    <div className="mx-auto flex w-full min-h-[100dvh] max-w-[min(100%,375px)] flex-col overflow-visible rounded-none shadow-none lg:min-h-0 lg:h-[480px] lg:w-[min(100%,960px)] lg:max-w-[960px] lg:flex-none lg:flex-row lg:overflow-hidden lg:rounded-[20px] lg:shadow-[0_6px_24px_rgba(0,0,0,0.08)]">
                        <div className="relative w-full shrink-0 overflow-hidden bg-slate-200/40 max-lg:h-[42dvh] max-lg:min-h-[38dvh] max-lg:max-h-[46dvh] lg:h-[480px] lg:min-h-0 lg:max-h-none lg:w-[540px] lg:flex-none lg:rounded-none">
                            <img
                                src={heroImageUrl}
                                alt=""
                                className="h-full w-full object-contain object-top lg:object-cover lg:object-center"
                            />
                            {/* Mobile: gradient bridge from hero into white text section */}
                            <div
                                className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[42%] min-h-[100px] bg-gradient-to-t from-white via-white/92 to-transparent lg:hidden"
                                aria-hidden
                            />
                        </div>

                        <aside
                            className="relative z-0 flex w-full flex-1 flex-col justify-start bg-white px-5 pb-8 pt-6 sm:px-6 max-lg:-mt-[2px] max-lg:min-h-0 max-lg:pb-2 max-lg:pt-0 max-lg:z-[2] lg:z-auto lg:mt-0 lg:min-h-0 lg:h-[480px] lg:w-[420px] lg:flex-none lg:shrink-0 lg:justify-center lg:rounded-none lg:px-8 lg:py-6"
                            style={{ backgroundColor: '#FFFFFF' }}
                        >
                            <div className="mx-auto flex w-full flex-col items-center text-center max-lg:relative max-lg:z-10 max-lg:-mt-[88px] max-lg:min-h-0 max-lg:flex-1 max-lg:justify-center max-lg:pt-[16px] lg:mt-0 lg:min-h-0 lg:flex-none lg:justify-center lg:overflow-y-auto lg:pt-0">
                                <div
                                    className={`${INNER_CONTENT_CLASS} flex flex-col items-stretch gap-0 lg:gap-3`}
                                >
                                    <h1 className="w-full shrink-0 text-balance text-center text-[18px] font-semibold leading-none tracking-[-0.005em] text-black max-lg:mb-6 lg:mb-0 lg:font-bold lg:leading-[1.2] lg:tracking-[-0.02em]">
                                        {title}
                                    </h1>

                                    <div
                                        className={`${BODY_FOOTER_TEXT_CLASS} ${legacySupportEmail ? 'max-lg:mb-2 lg:mb-0' : 'max-lg:mb-6 lg:mb-0'}`}
                                    >
                                        {bodyContent}
                                    </div>

                                    {legacySupportEmail ? (
                                        <div className="flex w-full shrink-0 flex-col items-center max-lg:mb-2 max-lg:gap-1 max-lg:py-0 lg:mb-0 lg:gap-1 lg:py-1">
                                            <span className="block text-center text-[12px] font-medium leading-[14px] text-slate-600 lg:text-[13px] lg:leading-[1.35]">
                                                {t('maintenance.webToCase.supportEmailLabel')}
                                            </span>
                                            <a
                                                className="block max-w-full break-words text-center text-[14px] font-normal leading-[14px] underline underline-offset-[2px] transition-opacity hover:opacity-90 lg:text-[14px] lg:leading-[1.35]"
                                                style={{ color: botButtonColor }}
                                                href={`mailto:${legacySupportEmail}`}
                                            >
                                                {legacySupportEmail}
                                            </a>
                                        </div>
                                    ) : null}

                                    <p className={BODY_FOOTER_TEXT_CLASS}>{footerContent}</p>

                                    <Button
                                        fns={[{ type: 'js', func: handleBackToHome }]}
                                        className="mt-4 w-full shrink-0 rounded-lg px-4 py-3 text-center text-[14px] font-semibold leading-none text-white transition-opacity hover:opacity-90 lg:mt-0"
                                        style={{
                                            backgroundColor: botButtonColor.slice(0, 7),
                                        }}
                                    >
                                        {buttonText}
                                    </Button>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WebToCaseMaintenancePage;
